import { Component, OnDestroy, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TimeagoModule } from 'ngx-timeago';
import { NgSelectModule } from '@ng-select/ng-select';

import { VisualDataComponent } from './visual-data/visual-data.component';

import { WalletAddressDirective } from '@/directives/wallet-address.directive';

import { DataService } from '@/services/data.service';
import { StateService } from '@/services/state.service';

import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FilterPipe } from '@/pipes/filter.pipe';
import { CalcPipe } from '@/pipes/calculate.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';

import { BehaviorSubject, Subject, map, switchMap, takeUntil, tap, timeout, of } from 'rxjs';

import { Event, EventType } from '@/models/graph';
import { UsdPipe } from '@/pipes/usd.pipe';

import { environment } from 'src/environments/environment';
import { Period } from '@/models/pipes';
import { ZERO_ADDRESS } from '@/constants/utils';

interface TxFilterItem { label: string, value: EventType };

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LazyLoadImageModule,
    TimeagoModule,
    NgSelectModule,
    ReactiveFormsModule,
    FormsModule,

    VisualDataComponent,
    WalletAddressDirective,

    TokenIdParsePipe,
    WeiToEthPipe,
    FilterPipe,
    CalcPipe,
    FormatCashPipe,
    UsdPipe,
  ],
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss']
})
export class TxOverviewComponent implements OnDestroy, OnInit {

  private events = new BehaviorSubject<Event[] | null>(null);
  events$ = this.events.asObservable();

  private topSales = new BehaviorSubject<Event[] | null>(null);
  topSales$ = this.topSales.asObservable();

  private destroy$ = new Subject<void>();

  txFilters: TxFilterItem[] = [
    { label: 'All', value: 'All' },
    { label: 'Bid Entered', value: 'BidEntered' },
    { label: 'Bid Withdrawn', value: 'BidWithdrawn' },
    // { label: 'Claimed', value: 'Claimed' },
    { label: 'Offer Withdrawn', value: 'OfferWithdrawn' },
    { label: 'Offered', value: 'Offered' },
    // TODO reactivate this when the subgraph is updated
    { label: 'Offered To', value: 'OfferedTo' },
    { label: 'Sale', value: 'Sale' },
    { label: 'Transferred', value: 'Transferred' },
    { label: 'Unwrapped', value: 'Unwrapped' },
    { label: 'Wrapped', value: 'Wrapped' },
  ];

  _activeTxFilter: string = this.txFilters[0].value;
  private activeTxFilter = new BehaviorSubject<TxFilterItem>(this.txFilters[0]);
  activeTxFilter$ = this.activeTxFilter.asObservable();

  labels: any = {
    BidEntered: 'New bid of',
    BidWithdrawn: 'Bid withdrawn',
    OfferWithdrawn: 'Offer withdrawn',
    Offered: 'Offered for',
    Sale: 'Bought for',
    Transferred: 'Transferred to',
    Unwrapped: 'Unwrapped by',
    Wrapped: 'Wrapped by',
  };

  env = environment;
  ZERO_ADDRESS = ZERO_ADDRESS;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public dataSvc: DataService,
    public stateSvc: StateService
  ) {
    console.log('[TxOverviewComponent] constructor called, isServer:', isPlatformServer(this.platformId));
  }

  ngOnInit() {
    console.log('[TxOverviewComponent] ngOnInit called, isServer:', isPlatformServer(this.platformId));

    // Add timeout for server-side to avoid hanging observables
    const serverTimeout = isPlatformServer(this.platformId) ? 15000 : 30000;

    this.stateSvc.activeTopSalesPeriod$.pipe(
      tap((period: Period) => console.log('[TxOverviewComponent] activeTopSalesPeriod changed:', period)),
      switchMap((period: Period) => {
        console.log('[TxOverviewComponent] Starting fetchTopSales with period:', period);
        return this.stateSvc.hideFlashLoans$.pipe(
          tap((hideFlashLoans: boolean) => console.log('[TxOverviewComponent] hideFlashLoans:', hideFlashLoans)),
          switchMap((hideFlashLoans: boolean) => {
            console.log('[TxOverviewComponent] Calling fetchTopSales...');
            return this.dataSvc.fetchTopSales(12, period, hideFlashLoans).pipe(
              timeout(serverTimeout),
              tap((topSales: Event[]) => console.log('[TxOverviewComponent] fetchTopSales result:', topSales?.length, 'items')),
              map((topSales: Event[]) => 
                hideFlashLoans ? topSales.filter(sale => !this.env.flaggedTransactions.includes(sale.transactionHash.toLowerCase())) : topSales
              ),
              map((topSales: Event[]) => topSales.slice(0, 12)),
              tap((topSales: Event[]) => {
                console.log('[TxOverviewComponent] Setting topSales:', topSales?.length, 'items');
                this.topSales.next(topSales);
              }),
              takeUntil(this.destroy$)
            )
          }),
          takeUntil(this.destroy$)
        )
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      console.log('[TxOverviewComponent] activeTopSalesPeriod$ subscription completed');
    });

    console.log('[TxOverviewComponent] Starting activeTxFilter$ subscription');
    this.activeTxFilter$.pipe(
      tap((filter: TxFilterItem) => console.log('[TxOverviewComponent] activeTxFilter changed:', filter)),
      switchMap((filter: TxFilterItem) => {
        console.log('[TxOverviewComponent] Calling fetchEvents with filter:', filter.value);
        return this.dataSvc.fetchEvents(12, 0, filter.value, 'blockTimestamp', 'desc').pipe(
          tap(() => console.log('[TxOverviewComponent] fetchEvents started ' + filter.value)),
          // Remove timeout to allow watchQuery to continue emitting
          tap(() => console.log('[TxOverviewComponent] fetchEvents completed ' + filter.value))
        );
      }),
      tap((events: Event[]) => {
        console.log('[TxOverviewComponent] fetchEvents result:', events?.length, 'items');
        this.events.next(events);
        console.log('[TxOverviewComponent] events.next() completed');
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        console.log('[TxOverviewComponent] activeTxFilter$ subscription next');
      },
      error: (error) => {
        console.error('[TxOverviewComponent] activeTxFilter$ subscription error:', error);
      },
      complete: () => {
        console.log('[TxOverviewComponent] activeTxFilter$ subscription completed');
      }
    });
  }

  trackByFn(i: number, item: any): string {
    return item.tokenId;
  }

  setActiveTxFilter(filter: TxFilterItem): void {
    this.activeTxFilter.next(filter);
  }

  setActiveTopSalesPeriod(period: Period): void {
    console.log('setActiveTopSalesPeriod', period);
    this.stateSvc.setActiveTopSalesPeriod(period);
  }

  toggleFlashLoans(): void {
    this.stateSvc.setHideFlashLoans(!this.stateSvc.getHideFlashLoans());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly environment = environment;
}
